import { zodResolver } from '@hookform/resolvers/zod';
import {
  passwordPolicySchema,
  userCreateSchema,
  userRoleSchema,
  type UserCreateInput,
} from '@plane/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Field } from '@/components/field';
import { useAuth } from '@/modules/auth/hooks/auth-context';
import { useCreateUser, useUpdateUser, useUsers } from '@/modules/users/hooks/use-users';
import { ApiRequestError } from '@/services/http-client';

/**
 * Formulário de edição.
 *
 * O e-mail não está aqui porque a API não permite trocá-lo: mudar a identidade
 * de acesso merece um fluxo com confirmação, e renomear a chave em silêncio
 * seria pior do que exigir uma conta nova.
 *
 * A senha é opcional — deixar em branco mantém a atual.
 */
const edicaoSchema = z.object({
  name: userCreateSchema.shape.name,
  role: userRoleSchema,
  password: z.union([z.literal(''), passwordPolicySchema]),
});

type EdicaoInput = z.infer<typeof edicaoSchema>;

function mensagemDeErro(erro: unknown): string {
  if (!(erro instanceof ApiRequestError)) {
    return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
  }

  if (erro.code === 'CONFLICT') return 'Já existe uma conta com este e-mail.';

  return erro.message;
}

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const criando = id === undefined;

  return criando ? <FormularioDeCriacao /> : <FormularioDeEdicao id={id} />;
}

function Casca({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex max-w-lg flex-col gap-6">
      <div>
        <Link to="/usuarios" className="text-sm text-brand-700 underline">
          ← Usuários
        </Link>
        <h1 className="mt-2 font-display text-2xl">{titulo}</h1>
      </div>

      {children}
    </div>
  );
}

function CampoPapel({
  registro,
  erro,
  desabilitado,
  aviso,
}: {
  registro: ReturnType<ReturnType<typeof useForm<never>>['register']> | object;
  erro?: string | undefined;
  desabilitado?: boolean;
  aviso?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="role" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
        Papel
      </label>

      <select
        {...registro}
        id="role"
        disabled={desabilitado}
        aria-describedby={erro ? 'role-error' : aviso ? 'role-aviso' : undefined}
        className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none disabled:bg-sand disabled:text-ink-soft"
      >
        <option value="EDITOR">Editor — só conteúdo</option>
        <option value="ADMIN">Administrador — conteúdo e usuários</option>
      </select>

      {aviso && (
        <p id="role-aviso" className="text-xs text-ink-soft">
          {aviso}
        </p>
      )}

      {erro && (
        <p id="role-error" role="alert" className="text-xs text-brass">
          {erro}
        </p>
      )}
    </div>
  );
}

function FormularioDeCriacao() {
  const navigate = useNavigate();
  const criar = useCreateUser();
  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserCreateInput>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: { name: '', email: '', password: '', role: 'EDITOR' },
  });

  const onSubmit = handleSubmit(async (valores) => {
    setErro(null);

    try {
      await criar.mutateAsync(valores);
      void navigate('/usuarios');
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  });

  return (
    <Casca titulo="Novo usuário">
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {erro && (
          <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
            {erro}
          </p>
        )}

        <Field
          id="name"
          label="Nome"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />

        <Field
          id="email"
          type="email"
          label="E-mail"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          id="password"
          type="password"
          label="Senha"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <CampoPapel registro={register('role')} erro={errors.role?.message} />

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-brand-600 px-5 py-3 text-sm text-bone transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Criando…' : 'Criar usuário'}
          </button>

          <Link to="/usuarios" className="text-sm text-ink-soft underline">
            Cancelar
          </Link>
        </div>
      </form>
    </Casca>
  );
}

function FormularioDeEdicao({ id }: { id: string }) {
  const navigate = useNavigate();
  const { usuario, sair } = useAuth();
  const atualizar = useUpdateUser();
  const [erro, setErro] = useState<string | null>(null);

  // A listagem já está em cache; reaproveitá-la evita uma rota `GET /users/:id`
  // que hoje não teria outro uso.
  const consulta = useUsers(1, 100);
  const alvo = consulta.data?.items.find((u) => u.id === id);

  const ehVoce = alvo?.id === usuario?.id;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EdicaoInput>({
    resolver: zodResolver(edicaoSchema),
    values: alvo ? { name: alvo.name, role: alvo.role, password: '' } : undefined,
  });

  const onSubmit = handleSubmit(async (valores) => {
    setErro(null);

    const trocouAPropriaSenha = ehVoce && valores.password !== '';

    try {
      await atualizar.mutateAsync({
        id,
        input: {
          name: valores.name,
          // Só manda o papel quando pode mudar; enviar o mesmo valor da própria
          // conta faria a API recusar sem necessidade.
          ...(ehVoce ? {} : { role: valores.role }),
          ...(valores.password === '' ? {} : { password: valores.password }),
        },
      });

      // Trocar a senha revoga todas as sessões, inclusive esta. O access token
      // em memória ainda valeria por até quinze minutos, então sem este logout
      // a pessoa continuaria navegando e seria desconectada de surpresa na
      // próxima renovação. Encerrar agora torna a consequência previsível.
      if (trocouAPropriaSenha) {
        await sair();
        void navigate('/login', { replace: true });

        return;
      }

      void navigate('/usuarios');
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  });

  if (consulta.isPending) {
    return (
      <Casca titulo="Editar usuário">
        <p role="status" aria-live="polite" className="text-sm text-ink-soft">
          Carregando…
        </p>
      </Casca>
    );
  }

  if (!alvo) {
    return (
      <Casca titulo="Editar usuário">
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Usuário não encontrado. Ele pode ter sido removido.
        </p>
      </Casca>
    );
  }

  return (
    <Casca titulo={`Editar ${alvo.name}`}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {erro && (
          <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
            {erro}
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-xs tracking-[0.12em] text-ink-soft uppercase">E-mail</span>
          <p className="border border-sand bg-sand px-3 py-2.5 text-sm text-ink-soft">
            {alvo.email}
          </p>
          <p className="text-xs text-ink-soft">
            O e-mail não pode ser alterado: ele é a identidade de acesso da conta.
          </p>
        </div>

        <Field
          id="name"
          label="Nome"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />

        <CampoPapel
          registro={register('role')}
          erro={errors.role?.message}
          desabilitado={ehVoce}
          {...(ehVoce ? { aviso: 'Você não pode alterar o seu próprio papel.' } : {})}
        />

        <Field
          id="password"
          type="password"
          label="Nova senha"
          autoComplete="new-password"
          placeholder="deixe em branco para manter a atual"
          error={errors.password?.message}
          {...register('password')}
        />

        <p className="text-xs text-ink-soft">
          {ehVoce
            ? 'Trocar a própria senha encerra esta sessão: você precisará entrar de novo.'
            : 'Trocar a senha ou o papel desconecta a pessoa de todas as sessões abertas.'}
        </p>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-brand-600 px-5 py-3 text-sm text-bone transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Salvando…' : 'Salvar'}
          </button>

          <Link to="/usuarios" className="text-sm text-ink-soft underline">
            Cancelar
          </Link>
        </div>
      </form>
    </Casca>
  );
}
